import { Group, Popover } from "@mantine/core"
import { DatePicker } from "@mantine/dates"
import { IconCalendar, IconX } from "@tabler/icons-react"
import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"
import { useEffect, useState } from "react"

import { DateInput } from "../../Inputs/DateInput/DateInput"
import { TextInput } from "../../Inputs/TextInput/TextInput"

dayjs.extend(customParseFormat)

const FORMAT = "DD/MM/YYYY"

function formatDate(date: Date | null) {
  return date ? dayjs(date).format(FORMAT) : ""
}

export type DateRangeValue = [Date | null, Date | null]

export type DatePickerFilterProps = {
  locale?: string
  value: DateRangeValue
  onChange: (range: DateRangeValue) => void
}

export const RangeDateFilterInput = ({
  locale = "fr",
  value,
  onChange,
}: DatePickerFilterProps) => {
  const [startDate, endDate] = value

  const [from, setFrom] = useState<string>(formatDate(startDate) ?? "")

  const [to, setTo] = useState<string>(formatDate(endDate) ?? "")

  const [error, setError] = useState<{
    from: string | null
    to: string | null
  }>({ from: null, to: null })

  useEffect(() => {
    const result: DateRangeValue = [startDate, endDate]

    if (from && dayjs(from, FORMAT).isValid()) {
      if (
        endDate &&
        dayjs(from, FORMAT).isAfter(dayjs(endDate)) &&
        from !== to
      ) {
        setFrom("")
        setError({ ...error, from: "Start date must be before end date" })
      } else {
        result[0] = dayjs(from, FORMAT).toDate()
        setError({ ...error, from: null })
      }
    }

    if (!from) {
      result[0] = null
    }

    if (!to) {
      result[1] = null
    }

    if (to && dayjs(to, FORMAT).isValid()) {
      if (
        startDate &&
        dayjs(to, FORMAT).isBefore(dayjs(startDate)) &&
        to !== from
      ) {
        setTo("")
        setError({ ...error, to: "End date must be after start date" })
      } else {
        result[1] = dayjs(to, FORMAT).toDate()
        setError({ ...error, to: null })
      }
    }

    onChange(result)
  }, [from, to, onChange])

  return (
    <Group grow>
      <Popover position="bottom-start" shadow="md">
        <Popover.Target>
          <Group>
            <TextInput
              w="8rem"
              placeholder="Pick a date"
              value={from}
              onChange={(e) => {
                setFrom(e.currentTarget.value)
              }}
              size="xs"
              rightSection={
                <IconX
                  size={16}
                  onClick={() => {
                    setFrom("")
                  }}
                  style={{ cursor: "pointer" }}
                />
              }
              leftSection={<IconCalendar size={12} />}
              error={error.from}
            />
            <TextInput
              w="8rem"
              placeholder="Pick a date"
              value={to}
              onChange={(e) => {
                setTo(e.currentTarget.value)
              }}
              rightSection={
                <IconX
                  size={16}
                  onClick={() => {
                    setTo("")
                  }}
                  style={{ cursor: "pointer" }}
                />
              }
              leftSection={<IconCalendar size={12} />}
              size="xs"
              error={error.to}
            />
          </Group>
        </Popover.Target>
        <Popover.Dropdown ml="0.8rem">
          <DatePicker
            type="range"
            locale={locale}
            value={[startDate, endDate]}
            onChange={([newStartDate, newEndDate]) => {
              setFrom(
                dayjs(newStartDate).isValid()
                  ? formatDate(dayjs(newStartDate).toDate())
                  : from || ""
              )
              setTo(
                dayjs(newEndDate).isValid()
                  ? formatDate(dayjs(newEndDate).toDate())
                  : to || ""
              )
            }}
          />
        </Popover.Dropdown>
      </Popover>
    </Group>
  )
}

export const DefaultDatePickerFilter = ({
  locale = "fr",
  value,
  onChange,
}: DatePickerFilterProps) => {
  const [startDate] = value

  return (
    <DateInput
      w="8rem"
      locale={locale}
      value={startDate}
      valueFormat={FORMAT}
      placeholder="Pick a date"
      onChange={(date) => onChange([date ? dayjs(date).toDate() : null, null])}
      clearable
      popoverProps={{
        withinPortal: true,
        closeOnClickOutside: false,
        trapFocus: false,
        portalProps: {
          target: document.body,
        },
      }}
    />
  )
}
